/**
 * chat v1 realtime — a single shared API-Gateway WebSocket.
 *
 * Each connect mints a fresh single-use ticket (`POST /ws-ticket`) and dials
 * `${ws}?ticket=<id>`; `$connect` is the only authorized route, so identity is fixed at
 * the handshake and every frame after is trusted from the server (CHAT-SERVICE.md §4.0).
 * Structure mirrors commandStream.ts: handshake timeout, exponential backoff reconnect, a
 * send queue that flushes on open, and — the load-bearing rule — an **open** signal that
 * fires on every (re)connect so subscribers can `backfill`-reconcile. A dropped frame
 * never arrives late; it only never arrives, so reconcile-on-open is the protocol, not a
 * nicety (§4.3).
 */
import { CHATV1 } from './config'
import { getTicket, type Envelope, type WireMessage } from './transport'
import { hasChatv1Token } from './token'

/** Frames the server pushes (§4.3). The client rolls back an optimistic row on `error`
 *  by `msg_id`, and dedupes `message` echoes of its own sends by `msg_id`. */
export type Frame =
  | {
      type: 'ack'
      msg_id: string
      conv_id: string
      seq: number
      ts: number
      /** A reply: the thread it went into and what that answers (chat wsSend) — maybe not the message pressed. */
      reply_to?: number
      reply_to_sender?: string
      reply_to_text?: string
      reply_to_agent?: string
      reply_to_agent_name?: string
      /** Sent from a whiteboard's chat: which board (chat messages.ts BoardFields). */
      board?: string
      board_name?: string
      /** About an area of it (chat messages.ts AreaFields). */
      area?: Record<string, unknown>
      /** Sent from a page's chat: which page (chat messages.ts PageFields). */
      page?: string
      page_name?: string
    }
  | {
      type: 'message'
      conv_id: string
      seq: number
      ts: number
      msg_id: string
      sender: string
      /** server-enriched from the sender's profile (§4.3), same as the history page */
      sender_name?: string | null
      sender_picture?: string | null
      envelope: Envelope
      via_agent?: string
      via_agent_name?: string | null
      reply_to?: number
      reply_to_sender?: string
      reply_to_text?: string
      reply_to_agent?: string
      reply_to_agent_name?: string
    }
  | {
      type: 'backfill'
      conv_id: string
      since_seq: number
      next_since: number
      complete: boolean
      messages: WireMessage[]
    }
  | { type: 'error'; code: string; msg_id?: string; conv_id?: string }
  /** Someone put an emoji on a message, or took theirs back (chat lambda/reactions.ts). Sent to
   *  every member, the reactor's other devices included; never to agents. */
  | { type: 'reaction'; conv_id: string; seq: number; emoji: string; user: string; on: boolean; ts: number }
  /** This person kept a message in a room, or let it go (chat lambda/bookmarks.ts). Sent to their own devices alone. */
  | { type: 'bookmark'; conv_id: string; seq: number; on: boolean; ts: number }
  /** Someone wrote to one of the room's whiteboards (chat lambda/boards.ts): the elements as they now are, or — when that
   *  would be too big for one frame — `reload`, and the board is read again. Never sent to agents. */
  | {
      type: 'board'
      conv_id: string
      board: string
      rev: number
      by: string
      via?: string
      client?: string
      elements?: import('../board/model').El[]
      reload?: boolean
    }
  /** A board was made or renamed (chat lambda/boards.ts): its row as the room's list says it. Never sent to agents. */
  | { type: 'boards'; conv_id: string; by: string; via?: string; boards: import('./transport').BoardInfo[] }
  /** This person has seen a board up to `rev` — on another device, or this one (chat lambda/boards.ts markSeen): its
   *  red dot goes away everywhere. Sent to this person alone. */
  | { type: 'board_seen'; conv_id: string; board: string; rev: number }
  /** Someone wrote to one of the room's pages (chat lambda/pages.ts): the blocks as they now are, or — too big for
   *  one frame — `reload`, and the page is read again. Never sent to agents. */
  | {
      type: 'page'
      conv_id: string
      page: string
      rev: number
      by: string
      via?: string
      client?: string
      blocks?: import('../page/pageModel').PageRow[]
      reload?: boolean
    }
  /** A page was made, renamed or deleted (`del`) (chat lambda/pages.ts). */
  | { type: 'pages'; conv_id: string; by: string; via?: string; pages: Array<import('./transport').PageInfo | { page: string; del: true }> }
  /** This person has seen a page up to `rev` (chat lambda/pages.ts). Sent to this person alone. */
  | { type: 'page_seen'; conv_id: string; page: string; rev: number }
  /** The viewer's project membership changed (added to / removed from one) —
   *  the client refetches its project list so the change appears live. */
  | { type: 'projects_changed'; conv_id: string }
  /** A game changed (a move, a hint, the end, a pairing) — the whole game as THIS person sees it (chat games.ts). */
  | {
      type: 'game'
      game: import('./transport').GameView
      /** What just happened and whether it was this person's own act (chat games.ts announce). */
      event?: { kind: 'invited' | 'accepted' | 'declined' | 'cancelled' | 'left' | 'said' | 'moved'; mine: boolean }
    }
  /** A mahjong table changed — the whole table as THIS person sees it (chat mahjongTable.ts); `seat`: who acted. */
  | {
      type: 'mahjong'
      game: import('./transport').MahjongView
      event?: { kind: 'invited' | 'accepted' | 'declined' | 'cancelled' | 'left' | 'said' | 'started'; mine: boolean; seat?: number }
    }

/** Actions the client sends (route selection is the `action` field). */
export type OutFrame =
  | {
      action: 'send'
      conv_id: string
      msg_id: string
      envelope: Envelope
      reply_to?: number
      board?: string
      /** About an area of that board (board/area.ts; chat messages.ts AreaFields). */
      area?: { x: number; y: number; w: number; h: number; ids: string[]; shot?: string }
    }
  | { action: 'ack'; conv_id: string; seq: number }
  | { action: 'read'; conv_id: string; seq: number }
  | { action: 'backfill'; conv_id: string; since_seq: number; limit?: number }

const HANDSHAKE_MS = 15_000
const BACKOFF_MIN = 500
const BACKOFF_MAX = 15_000

let sock: WebSocket | null = null
let opening = false
let wantOpen = false
let backoff = BACKOFF_MIN
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let queue: OutFrame[] = []
const frameListeners = new Set<(f: Frame) => void>()
const openListeners = new Set<() => void>()

export function onChatv1Frame(fn: (f: Frame) => void): () => void {
  frameListeners.add(fn)
  return () => frameListeners.delete(fn)
}

/** Fires on every (re)open — subscribers MUST reconcile (backfill) here. */
export function onChatv1Open(fn: () => void): () => void {
  openListeners.add(fn)
  return () => openListeners.delete(fn)
}

/** Send an action; if the socket isn't open yet it's queued and flushed on connect. */
export function sendChatv1(frame: OutFrame): void {
  if (sock?.readyState === WebSocket.OPEN) {
    sock.send(JSON.stringify(frame))
    return
  }
  queue.push(frame)
  connectChatv1()
}

/**
 * Send a message and await the server's verdict for it — the ack (with its seq) on
 * success, or a reject carrying the backend's error CODE on failure. The socket is
 * otherwise fire-and-forget; this is for the few sends whose outcome the caller must
 * act on, e.g. a file send racing its S3-completion event (`attachment_invalid` →
 * retry). Correlated by msg_id; times out so a dropped frame can't hang the caller.
 */
export function sendChatv1Await(
  frame: Extract<OutFrame, { action: 'send' }>,
  timeoutMs = 12_000,
): Promise<{ seq: number; ts: number }> {
  return new Promise((resolve, reject) => {
    let settled = false
    const finish = (fn: () => void) => {
      if (settled) return
      settled = true
      off()
      clearTimeout(timer)
      fn()
    }
    const off = onChatv1Frame((f) => {
      if (f.type === 'ack' && f.msg_id === frame.msg_id) {
        finish(() => resolve({ seq: f.seq, ts: f.ts }))
      } else if (f.type === 'error' && f.msg_id === frame.msg_id) {
        finish(() => reject(new Error(f.code)))
      }
    })
    const timer = setTimeout(() => finish(() => reject(new Error('send_timeout'))), timeoutMs)
    sendChatv1(frame)
  })
}

/** Ensure the socket is (being) opened. Idempotent; a no-op until a token exists. */
export function connectChatv1(): void {
  wantOpen = true
  if (sock || opening) return
  if (!hasChatv1Token()) return // bootstrap seeds the token first, then calls again
  opening = true
  void openSocket()
}

/** Tear down and stop reconnecting (sign-out). */
export function disconnectChatv1(): void {
  wantOpen = false
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  const s = sock
  sock = null
  opening = false
  queue = []
  if (s) {
    try {
      s.close()
    } catch {
      /* already closing */
    }
  }
}

async function openSocket(): Promise<void> {
  let ticket: string
  try {
    ticket = (await getTicket()).ticket
  } catch {
    // No token / expired / device not registered — back off and let bootstrap catch up.
    opening = false
    scheduleReconnect()
    return
  }
  const s = new WebSocket(`${CHATV1.ws}?ticket=${encodeURIComponent(ticket)}`)
  const hs = setTimeout(() => {
    try {
      s.close()
    } catch {
      /* ignore */
    }
  }, HANDSHAKE_MS)

  s.onopen = () => {
    clearTimeout(hs)
    sock = s
    opening = false
    backoff = BACKOFF_MIN
    for (const f of queue.splice(0)) {
      try {
        s.send(JSON.stringify(f))
      } catch {
        /* will be re-driven on the next reconcile */
      }
    }
    for (const fn of [...openListeners]) fn()
  }
  s.onmessage = (m) => {
    let f: Frame
    try {
      f = JSON.parse(typeof m.data === 'string' ? m.data : '') as Frame
    } catch {
      return
    }
    for (const fn of [...frameListeners]) fn(f)
  }
  s.onerror = () => {
    try {
      s.close()
    } catch {
      /* ignore */
    }
  }
  s.onclose = () => {
    clearTimeout(hs)
    if (sock === s) sock = null
    opening = false
    scheduleReconnect()
  }
}

function scheduleReconnect(): void {
  if (!wantOpen || reconnectTimer) return
  const wait = backoff
  backoff = Math.min(backoff * 2, BACKOFF_MAX)
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    if (wantOpen && !sock && !opening) {
      opening = true
      void openSocket()
    }
  }, wait)
}

/**
 * Foreground reconciliation.
 *
 * A backgrounded tab — especially an installed PWA on mobile — has its socket
 * SUSPENDED by the OS, and suspension does NOT fire `onclose`. So on return the
 * browser holds a zombie that still reads `OPEN` but delivers nothing: new messages
 * sent while away never arrive, and there is no close to trigger a reconnect. The
 * symptom is exactly "I had to refresh to see it".
 *
 * The cure is the one the protocol already relies on — reconcile-on-(re)open (§4.3).
 * When the page becomes visible after being hidden we REPLACE the socket outright
 * (the zombie can't be trusted), and the fresh connection's `open` fires
 * `onChatv1Open`, where every view backfills everything past its high-water. A missed
 * frame never arrives late; it arrives here, as a reconcile.
 */
let wasHidden = false
function handleVisibility(): void {
  if (typeof document === 'undefined') return
  if (document.visibilityState !== 'visible') {
    wasHidden = true
    return
  }
  if (!wasHidden) return
  wasHidden = false
  if (!wantOpen) return
  const stale = sock
  sock = null
  opening = false
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  backoff = BACKOFF_MIN
  if (stale) {
    // Drop its handlers first so its late `close` can't schedule a competing reconnect.
    stale.onclose = null
    stale.onmessage = null
    stale.onerror = null
    try {
      stale.close()
    } catch {
      /* already closing */
    }
  }
  connectChatv1()
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', handleVisibility)
  // A network flap (e.g. wifi→cellular hand-off on a phone) silently kills the socket
  // the same way; treat coming back online as a foreground for reconciliation.
  window.addEventListener('online', () => {
    wasHidden = true
    handleVisibility()
  })
}
